import React, { useState, useEffect } from "react";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function FPS_Per() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMonthYear();
  }, []);

  const fetchMonthYear = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_URL}/api/fpsDistribution/MonthAndYear`);
      setData(res.data);
    } catch (err) {
      console.error("Error fetching month/year", err);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async (month, year) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL}/api/fpsDistribution/fpsDistributionList`, {
        params: { month, year }
      });

      const fpsData = res.data;

      if (!fpsData || fpsData.length === 0) {
        alert("No data to download");
        return;
      }

      // 🔥 Dynamic commodity keys
      const commodityKeys = Object.keys(fpsData[0].commodityData || {});

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("FPS Distribution");

      // 🔥 Title
      const totalColumns = 13 + commodityKeys.length;
      const titleRow = sheet.addRow([`${monthNames[month - 1]}-${year} FPS wise Distribution %`]);

      sheet.mergeCells(1, 1, 1, totalColumns);

      titleRow.eachCell(cell => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFA500" } };
        cell.font = { bold: true, size: 14 };
        cell.alignment = { horizontal: "center" };
      });

      // 🔥 Header Row 1
      const headerRow1 = sheet.addRow([
        "Sl.No", "District", "Office", "FPS", "Device Id",
        "Cards", "", "",
        "Total Transactions", "", "", "", "",
        ...Array(commodityKeys.length).fill("Distribution")
      ]);

      // 🔥 Header Row 2
      const headerRow2 = sheet.addRow([
        "", "", "", "", "",
        "Total", "Distributed", "Distributed %",
        "Total", "Biometric", "Iris", "Mobile OTP", "OTP",
        ...commodityKeys.map(k => k.toUpperCase())
      ]);

      // 🔥 Merging
      sheet.mergeCells(2, 1, 3, 1);
      sheet.mergeCells(2, 2, 3, 2);
      sheet.mergeCells(2, 3, 3, 3);
      sheet.mergeCells(2, 4, 3, 4);
      sheet.mergeCells(2, 5, 3, 5);
      sheet.mergeCells(2, 6, 2, 8);
      sheet.mergeCells(2, 9, 2, 13);

      if (commodityKeys.length > 0) {
        sheet.mergeCells(2, 14, 2, 13 + commodityKeys.length);
      }

      // 🔥 Style headers
      [headerRow1, headerRow2].forEach(row => {
        row.eachCell(cell => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFA500" } };
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          cell.border = {
            top: { style: "thin" }, left: { style: "thin" },
            bottom: { style: "thin" }, right: { style: "thin" }
          };
        });
      });

      // 🔥 Data rows
      fpsData.forEach((fps, idx) => {
        const distributedPercent =
          fps.totalCards > 0 ? (fps.availCards * 100) / fps.totalCards : 0;

        const commodityValues = commodityKeys.map(
          key => (fps.commodityData[key] || 0).toFixed(3)
        );

        const row = sheet.addRow([
          idx + 1,
          fps.distNameEn,
          fps.afsoNameEn,
          fps.fpsId,
          fps.deviceId,
          fps.totalCards,
          fps.availCards,
          distributedPercent.toFixed(2),
          fps.availTrans,
          fps.availBioTrans || 0,
          fps.availIrisTrans || 0,
          fps.availMotpTrans || 0,
          fps.availOtpTrans || 0,
          ...commodityValues
        ]);

        row.eachCell((cell, colNumber) => {
          if (colNumber > 5) {
            cell.alignment = { horizontal: "right" };
          }
        });
      });

      // 🔥 Column width dynamic
      const baseWidths = [8, 20, 20, 15, 15, 12, 12, 12, 15, 15, 15, 15, 15];
      const commodityWidths = Array(commodityKeys.length).fill(15);

      sheet.columns = [...baseWidths, ...commodityWidths].map(w => ({ width: w }));

      // 🔥 Export
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `FPS_Per_${month}_${year}.xlsx`);

    } catch (err) {
      console.error("Error generating Excel:", err);
      alert("Error generating Excel file");
    }
  };

  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      <table
        border="1"
        cellPadding="6"
        style={{ width: "30%", margin: "0 auto", borderCollapse: "collapse", textAlign: "center" }}
      >
        <thead>
          <tr style={{ backgroundColor: "black", color: "white", height: "50px" }}>
            <th colSpan="4">FPS Distribution</th>
          </tr>
          <tr style={{ backgroundColor: "black", color: "white", height: "40px" }}>
            <th>S.No</th>
            <th>Month</th>
            <th>Year</th>
            <th>Link</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="4">Loading...</td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan="4" style={{ color: "red", fontWeight: "bold" }}>No Data Found</td>
            </tr>
          ) : (
            data.map((item, idx) => (
              <tr key={idx} style={{ height: "30px" }}>
                <td>{idx + 1}</td>
                <td>{monthNames[item.month - 1]}</td>
                <td>{item.year}</td>
                <td>
                  <button
                    onClick={() => downloadExcel(item.month, item.year)}
                    style={{
                      textDecoration: "underline",
                      color: "#0033CC",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "bold"
                    }}
                  >
                    Download
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default FPS_Per;
